@if (user(); as u) {
  <div class="flex flex-col gap-4">
    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Nombre completo</p>
            <p class="text-sm font-semibold text-slate-900">{{ getDisplayName(u) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">ID</p>
            <p class="text-sm text-slate-700 font-mono">{{ u.id ?? '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Tipo</p>
            <span
              class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
              [class.bg-purple-100]="u.type === UserType.OWNER"
              [class.text-purple-800]="u.type === UserType.OWNER"
              [class.bg-slate-100]="u.type === UserType.USER"
              [class.text-slate-800]="u.type === UserType.USER">
              {{ getTypeLabel(u.type) }}
            </span>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Email</p>
            @if (u.email) {
              <a [href]="'mailto:' + u.email" class="text-sm text-sky-600 hover:text-sky-800">{{ u.email }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Teléfono</p>
            @if (u.phone) {
              <a [href]="'tel:' + u.phone" class="text-sm text-sky-600 hover:text-sky-800">{{ u.phone }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Nombre</p>
            <p class="text-sm text-slate-700">{{ u.name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Apellido</p>
            <p class="text-sm text-slate-700">{{ u.lastname || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Apellido segundo</p>
            <p class="text-sm text-slate-700">{{ u.surname || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Cargo</p>
            <p class="text-sm text-slate-700">{{ u.jobTitle || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Estado</p>
            <span
              class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
              [class.bg-green-100]="u.state === 'ACTIVE'"
              [class.text-green-800]="u.state === 'ACTIVE'"
              [class.bg-red-100]="u.state === 'INACTIVE'"
              [class.text-red-800]="u.state === 'INACTIVE'">
              {{ u.state === 'ACTIVE' ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha de creación</p>
            <p class="text-sm text-slate-700">{{ u.createdAt ? (u.createdAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Última actualización</p>
            <p class="text-sm text-slate-700">{{ u.updatedAt ? (u.updatedAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
          <i class="pi pi-sitemap text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Roles ({{ roles().length }})</span>
      </div>
      @if (rolesLoading()) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-500"></i>
          <p class="text-sm text-slate-500">Cargando roles...</p>
        </div>
      } @else if (roles().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">Sin roles asignados.</p>
      } @else {
        <div class="divide-y divide-slate-100">
          @for (role of roles(); track role.value) {
            <div class="flex items-start gap-2 px-4 py-2">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <span class="text-xs font-medium text-slate-900 truncate">{{ role.value }}</span>
                  @if (role.global) {
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Global</span>
                  }
                  @if (role.system) {
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Sistema</span>
                  }
                </div>
                @if (role.description) {
                  <p class="text-[11px] text-slate-500 mt-0.5">{{ role.description }}</p>
                }
                @if (role.permissions?.length) {
                  <p class="text-[11px] text-slate-500 mt-0.5">
                    {{ role.permissions.length }} permiso{{ role.permissions.length === 1 ? '' : 's' }}
                  </p>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-key text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Privilegios ({{ permissions().length }})</span>
      </div>
      @if (permissionsLoading()) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-500"></i>
          <p class="text-sm text-slate-500">Cargando privilegios...</p>
        </div>
      } @else if (permissions().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">Sin privilegios asignados.</p>
      } @else {
        <div class="divide-y divide-slate-100">
          @for (group of permissionsByContext(); track group.context) {
            <div class="px-4 py-2">
              <p class="text-[11px] font-semibold text-slate-600 uppercase mb-1.5">{{ group.label }}</p>
              <div class="space-y-1.5">
                @for (perm of group.permissions; track perm.value) {
                  <div class="flex items-start gap-2 min-w-0">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="text-xs font-medium text-slate-900 font-mono">{{ perm.value }}</span>
                        @if (perm.global) {
                          <span class="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Global</span>
                        }
                      </div>
                      @if (perm.description) {
                        <p class="text-[11px] text-slate-500 mt-0.5">{{ perm.description }}</p>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
}
